import type { User, UserHome } from "@/common/types/user";
import { defineStore } from "pinia";
import { RouteNames } from "@/router";
import { usePermissions } from "@/common/permission";
import { authAPI } from "@/api/auth";
import type { LoginResult } from "@/common/types/result";
import { LoginStatus } from "@/common/types/result";
import { usersAPI } from "@/api/users";
import Config from "@/common/config";
import { verifyToken } from "@/common/helpers";
import { computed, ref, watch } from "vue";
import type { AxiosError } from "axios";
import { useLocaleStore } from "@/stores/locale";
import { defineRulesFor, useEtcAbility } from "@/common/ability";
import { encryptStorage } from "../utils/storage";
import { useQuotationStore } from "@/stores/quotation";
import { useEtCoreAuthStore } from "@/stores/etCoreAuth";
import type { UserWidget } from "@/common/types/widget";

export const useAuthStore = defineStore(
  "auth",
  () => {
    // State
    const token = ref<string>();
    const currentUser = ref<User>();
    const impersonatingUserToken = ref<string>();
    const quotationStore = useQuotationStore();
    const etCoreAuthStore = useEtCoreAuthStore();
    const permissions = usePermissions();
    const ability = useEtcAbility();
    const localeStore = useLocaleStore();
    const isHydrated = ref(false);
    const isInitialized = ref(false);
    const hasAuthenticatedInteractively = ref(false);

    let initializationPromise: Promise<boolean> | undefined;

    watch(
      currentUser,
      (newUser) => {
        if (newUser !== undefined) {
          // set the permissions and abilities
          permissions.updatePermissions(newUser);
          ability.update(defineRulesFor(newUser));

          // set the user language
          const userLanguage = newUser.settings?.userLanguage;
          if (userLanguage !== null && userLanguage !== undefined) {
            localeStore.locale = userLanguage;
          }
        } else {
          // clear the permissions and abilities
          permissions.clearPermissions();
          ability.update([]);
        }
      },
      { immediate: true, deep: true },
    );

    const isSignedIn = computed(() => token.value !== undefined);

    const isActivated = computed(() => {
      const status = currentUser.value?.personalData?.status;
      return status !== undefined && status == "active";
    });

    const isImpersonationActive = computed(
      () => impersonatingUserToken.value !== undefined,
    );

    const userHomeRouteName = computed(() => {
      const home = currentUser.value?.settings?.home;

      return home ? mapUserHomeToRouteName(home, currentUser.value) : undefined;
    });

    // Actions
    const performLogin = async (
      username: string,
      password: string,
      otp?: string,
    ): Promise<LoginResult> => {
      return authAPI
        .login(username, password, otp)
        .then(async (response) => {
          const token = response.data.access_token;

          switch (response.status) {
            case 200: {
              const setStateSuccess =
                await verifyAndSetTokenAndCurrentUser(token);

              if (!setStateSuccess) {
                return { status: LoginStatus.INTERNAL_ERROR };
              }

              hasAuthenticatedInteractively.value = true;

              if (Config.isCompass) {
                // ET Core authentication is best-effort. ETC remains the primary
                // Compass session, so an unavailable Keycloak/ET Core auth flow
                // must not prevent access to features that do not use ET Core.
                await etCoreAuthStore.loginWithPassword(username, password);
              }

              return { status: LoginStatus.SUCCESS };
            }
            case 292:
              return {
                status: LoginStatus.TERMS_OF_USE_ACCEPT_REQUIRED,
                token,
              };
            case 293:
              return { status: LoginStatus.PASSWORD_CHANGE_REQUIRED, token };
            case 295:
              return {
                status: LoginStatus.PRIVACY_POLICY_ACCEPT_REQUIRED,
                token,
              };
            case 296:
              return {
                status: LoginStatus.OTP_REQUIRED,
                token,
              };
            case 297:
              return {
                status: LoginStatus.OTP_REQUIRED_PERIODICALLY,
                token,
              };
            default:
              return { status: LoginStatus.SUCCESS };
          }
        })
        .catch((error) => {
          console.log("error.response.status" + error.response.status);

          switch (error.response.status) {
            case 401:
              clearTokenAndUser();
              return { status: LoginStatus.INVALID_CREDENTIALS };
            case 490:
              return { status: LoginStatus.DISABLED_USER };
            case 491:
              return { status: LoginStatus.NEW_USER };
            case 494:
              return { status: LoginStatus.EMAIL_CONFIRM };
            case 500:
              clearTokenAndUser();
              return { status: LoginStatus.INTERNAL_ERROR };
            default:
              clearTokenAndUser();
              return { status: LoginStatus.INTERNAL_ERROR };
          }
        });
    };

    const performLogout = (token?: string) => {
      return authAPI
        .logout(token)
        .then(() => {
          clearTokenAndUser();
          void etCoreAuthStore.logout();
          localStorage.removeItem(Config.userManagementDataPersistKey);
          localStorage.removeItem(Config.chargeweightDataPersistKey);
          localStorage.removeItem(Config.quotationDataPersistKey);
          quotationStore.resetQuotationData();
          return true;
        })
        .catch((error: AxiosError) => {
          switch (error.response?.status) {
            case 401: // there was no valid session to begin with, so just continue as if the logout was successful
              clearTokenAndUser();
              void etCoreAuthStore.logout();
              return true;
            case 500:
              return false;
            default:
              return false;
          }
        });
    };
    const startImpersonate = (impersonateUserID: string) => {
      return usersAPI
        .impersonateByID(impersonateUserID)
        .then(async (response) => {
          const impersonateUserToken = response.data.access_token;

          impersonatingUserToken.value = token.value;

          const setImpersonatedUserSuccess =
            await verifyAndSetTokenAndCurrentUser(impersonateUserToken);

          if (!setImpersonatedUserSuccess) {
            // something went wrong, set the original user as the current user and clear the impersonating user token.
            await verifyAndSetTokenAndCurrentUser(impersonatingUserToken.value);
            impersonatingUserToken.value = undefined;
          }

          return setImpersonatedUserSuccess;
        })
        .catch(() => {
          return false;
        });
    };

    const stopImpersonate = () => {
      if (impersonatingUserToken.value !== undefined) {
        return performLogout().then(async (success) => {
          if (success) {
            await verifyAndSetTokenAndCurrentUser(impersonatingUserToken.value);
            impersonatingUserToken.value = undefined;
            return true;
          } else {
            return false;
          }
        });
      }
      // there is no impersonation currently, so act like ending the impersonation was successful
      return new Promise((resolve, _) => resolve(true));
    };

    const clearTokenAndUser = () => {
      token.value = undefined;
      currentUser.value = undefined;
      hasAuthenticatedInteractively.value = false;
    };

    const verifyAndSetTokenAndCurrentUser = async (
      newToken: string | undefined,
    ) => {
      let success;

      if (newToken !== undefined) {
        try {
          const { payload } = await verifyToken(newToken);

          try {
            token.value = newToken;

            if (payload.sub !== undefined) {
              currentUser.value = (
                await usersAPI.getUserByID(payload.sub)
              ).data;
              isHydrated.value = true;

              success = true;
            } else {
              // no subject set in token
              success = false;
            }
          } catch (err) {
            // getting and setting the user caused some error
            success = false;
          }
        } catch (err) {
          // the token is invalid
          success = false;
        }
      } else {
        // the token is undefined
        success = false;
      }

      if (!success) {
        clearTokenAndUser();
      }

      isInitialized.value = true;
      return success;
    };

    const ensureInitialized = async (): Promise<boolean> => {
      if (isInitialized.value) {
        return isSignedIn.value && currentUser.value !== undefined;
      }

      if (initializationPromise !== undefined) {
        return initializationPromise;
      }

      initializationPromise = verifyAndSetTokenAndCurrentUser(
        token.value,
      ).finally(() => {
        initializationPromise = undefined;
      });

      return initializationPromise;
    };

    const updateWidgets = (widgets: UserWidget[]) => {
      if (!currentUser.value) return;
      currentUser.value = { ...currentUser.value, widgets };
    };

    return {
      token,
      currentUser,
      impersonatingUserToken,
      isSignedIn,
      isActivated,
      isImpersonationActive,
      userHomeRouteName,
      performLogin,
      performLogout,
      startImpersonate,
      stopImpersonate,
      clearTokenAndUser,
      verifyAndSetTokenAndCurrentUser,
      isHydrated,
      ensureInitialized,
      hasAuthenticatedInteractively,
      updateWidgets,
    };
  },
  {
    //@ts-ignore types not compatible, but works
    persist: {
      storage: encryptStorage,
      paths: ["token"],
      key: Config.authDataPersistKey,
      afterRestore: (context) => {
        const store = context.store as ReturnType<typeof useAuthStore>;

        store.ensureInitialized().then((success) => {
          if (!success) {
            // Token must be broken/manipulated. Clear the token and the user
            store.clearTokenAndUser();
          }
        });
      },
    },
  },
);

const mapUserHomeToRouteName = (home: UserHome, user?: User): RouteNames => {
  const { permissionPresent } = usePermissions();
  const compassInformation = user?.compassInformation;
  const quoteToolCompanyPermissions = ["TUS010010", "TUS010011"];
  const companyPermissionPresent = (permissionName: string) =>
    compassInformation?.companyPermissions?.some(
      ({ permission }) => permission === permissionName,
    ) ?? false;
  const as400PermissionPresent = (permissionName: string) =>
    compassInformation?.permissions?.includes(permissionName) ?? false;

  if (home === "dashboard") {
    return RouteNames.DASHBOARD;
  } else if (
    home === "userProfile" &&
    permissionPresent("DASHBOARD_USERMENU_SETTINGS_DISPLAY")
  ) {
    return RouteNames.USER_PROFILE;
  } else if (
    home === "userManagement" &&
    permissionPresent("DASHBOARD_USERMENU_USERMANAGEMENT_DISPLAY")
  ) {
    return RouteNames.USER_MANAGEMENT;
  } else if (
    home === "loginInfo" &&
    permissionPresent("DASHBOARD_USERMENU_MAINTENANCE_DISPLAY")
  ) {
    return RouteNames.LOGIN_INFO_EDIT;
  } else if (home === "itr" && permissionPresent("REPORT_ITR")) {
    return RouteNames.REPORT_ITR;
  } else if (home === "oor" && permissionPresent("REPORT_OOR")) {
    return RouteNames.REPORT_OOR;
  } else if (home === "wir" && permissionPresent("REPORT_WIR")) {
    return RouteNames.REPORT_WIR;
  } else if (
    home === "imsIncoming" &&
    permissionPresent("REPORT_WIR_IM_VIEW")
  ) {
    return RouteNames.INVENTORY_MANAGEMENT_INCOMING;
  } else if (
    home === "imsOutgoing" &&
    permissionPresent("REPORT_WIR_IM_VIEW")
  ) {
    return RouteNames.INVENTORY_MANAGEMENT_OUTGOING;
  } else if (
    home === "imsReceived" &&
    permissionPresent("REPORT_WIR_IM_VIEW")
  ) {
    return RouteNames.INVENTORY_MANAGEMENT_RECEIVED;
  } else if (home === "imsSent" && permissionPresent("REPORT_WIR_IM_VIEW")) {
    return RouteNames.INVENTORY_MANAGEMENT_SENT;
  } else if (
    home === "imsInventory" &&
    permissionPresent("REPORT_WIR_IM_VIEW")
  ) {
    return RouteNames.INVENTORY_MANAGEMENT_INVENTORY;
  } else if (home === "chargeWeightCalculator") {
    return RouteNames.CHARGE_WEIGHT_CALCULATOR;
  } else if (
    home === "newOffer" &&
    !Config.isCompass &&
    permissionPresent("QUOTE_VIEW")
  ) {
    return RouteNames.NEW_OFFER;
  } else if (
    home === "quoteTool" &&
    Config.isCompass &&
    quoteToolCompanyPermissions.some(companyPermissionPresent)
  ) {
    return RouteNames.QUOTE_TOOL;
  } else if (home === "address" && Config.isCompass) {
    return RouteNames.ADDRESS;
  } else if (home === "debtorCreditor" && Config.isCompass) {
    return RouteNames.CUSTOMER_ADDRESS;
  } else if (
    home === "permissionUsageQuery" &&
    Config.isCompass &&
    as400PermissionPresent("MANAGE_USERS")
  ) {
    return RouteNames.PERMISSION_USAGE_QUERY;
  } else if (home === "translationTool" && Config.isCompass) {
    return RouteNames.TRANSLATION_TOOL;
  } else if (home === "truckDispositionMap" && Config.isCompass) {
    return RouteNames.TRUCK_DISPOSITION_MAP;
  } else {
    return RouteNames.DASHBOARD;
  }
};
